// Utilidades y estilos compartidos del modulo de Reportes.
// Disenado para ser 100% responsive: flexbox/grid con wrap, tablas en overflowX,
// sin posicionamiento absoluto que pueda montar un componente sobre otro.
import React from 'react';
import { COL } from 'shared/darkColors';
import { Delta } from 'shared/components/Dashboard/charts';
import Spinner from 'shared/components/Spinner';

// ── Formateadores (re-exportados de charts para un solo import) ──
export const money = (n) =>
  '$' +
  (Number(n) || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const intFmt = (n) => (Number(n) || 0).toLocaleString('es-VE');
export const pctFmt = (n) => `${(Number(n) || 0).toFixed(1)}%`;

export const fechaISO = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const PRESETS = [
  { id: '30d', label: '30 días' },
  { id: '7d', label: '7 días' },
  { id: 'hoy', label: 'Hoy' },
  { id: 'mes', label: 'Este mes' },
  { id: 'mesant', label: 'Mes anterior' },
  { id: 'todo', label: 'Todo' },
  { id: 'custom', label: 'Personalizado' },
];

export function rangoDePreset(id) {
  const hoy = new Date();
  if (id === 'hoy') return [fechaISO(hoy), fechaISO(hoy)];
  if (id === '7d') {
    const a = new Date(hoy);
    a.setDate(a.getDate() - 6);
    return [fechaISO(a), fechaISO(hoy)];
  }
  if (id === '30d') {
    const a = new Date(hoy);
    a.setDate(a.getDate() - 29);
    return [fechaISO(a), fechaISO(hoy)];
  }
  if (id === 'mes') {
    const a = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    return [fechaISO(a), fechaISO(hoy)];
  }
  if (id === 'mesant') {
    const a = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    const b = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
    return [fechaISO(a), fechaISO(b)];
  }
  return ['', ''];
}

// Convierte los filtros globales a URLSearchParams comunes a todos los reportes.
export function buildQuery(filtros, extra = {}) {
  const p = new URLSearchParams();
  if (filtros.sede) p.set('sede', filtros.sede);
  if (filtros.fechaDesde) p.set('fecha_desde', filtros.fechaDesde);
  if (filtros.fechaHasta) p.set('fecha_hasta', filtros.fechaHasta);
  if (filtros.departamento) p.set('departamento', filtros.departamento);
  Object.entries(extra).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  });
  return p;
}

// ── Estilos base ──
export const S = {
  page: { padding: '20px', minHeight: '100%', boxSizing: 'border-box' },
  // Fila: rail + contenido. En desktop el rail es columna fija; en mobile se apila.
  layout: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  rail: {
    flex: '0 0 220px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: '0',
  },
  railMobile: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    overflowX: 'auto',
    paddingBottom: '6px',
    flexShrink: 0,
  },
  content: { flex: '1 1 0', minWidth: '0', width: '100%' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
  },
  title: { fontSize: '19px', fontWeight: 800, color: COL.pri },
  subtitle: { fontSize: '12px', color: COL.muted, marginTop: '2px' },
  select: {
    padding: '7px 10px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    fontSize: '12px',
    background: COL.white,
    fontFamily: 'inherit',
    color: COL.text,
    maxWidth: '100%',
  },
  input: {
    padding: '7px 10px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    fontSize: '12px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    background: COL.white,
    color: COL.text,
  },
  btn: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    fontSize: '12px',
    fontWeight: 600,
    background: COL.white,
    color: COL.muted,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  btnActive: { background: COL.pri2, color: COL.white, borderColor: COL.pri2 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '560px' },
  th: {
    padding: '8px 10px',
    textAlign: 'left',
    fontSize: '10px',
    fontWeight: 700,
    color: COL.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    borderBottom: `2px solid ${COL.border}`,
    whiteSpace: 'nowrap',
  },
  td: { padding: '8px 10px', borderBottom: `1px solid ${COL.border}`, fontSize: '12px' },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '10px',
    marginBottom: '14px',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '14px',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    padding: '1px 7px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 700,
  },
  tableWrap: { overflowX: 'auto', width: '100%' },
};

// ── Componentes compartidos ──
export function Kpi({ label, value, sub, color = COL.pri, delta }) {
  return (
    <div
      style={{
        background: COL.white,
        border: `1px solid ${COL.border}`,
        borderRadius: '10px',
        padding: '12px 14px',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ fontSize: '11px', color: COL.muted, fontWeight: 600, marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '18px', fontWeight: 800, color }}>{value}</span>
        {delta && <Delta actual={delta.actual} anterior={delta.anterior} />}
      </div>
      {sub != null && sub !== '' && (
        <div style={{ fontSize: '11px', color: COL.muted, marginTop: '3px' }}>{sub}</div>
      )}
    </div>
  );
}

export function SectionCard({ title, icon: Icon, color = COL.pri, hint, children, style }) {
  return (
    <div
      style={{
        background: COL.white,
        border: `1px solid ${COL.border}`,
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '14px',
        minWidth: 0,
        boxSizing: 'border-box',
        ...style,
      }}
    >
      {title && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '14px',
            flexWrap: 'wrap',
          }}
        >
          {Icon && <Icon size={15} color={color} />}
          <span style={{ fontSize: '14px', fontWeight: 700, color: COL.pri }}>{title}</span>
          {hint && (
            <span style={{ fontSize: '11px', color: COL.muted }}>{hint}</span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

export function Cargando() {
  return <Spinner />;
}

export function ErrorMsg({ message = 'No se pudo cargar el reporte.' }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '24px',
        color: COL.red,
        fontSize: '13px',
        background: 'var(--agx-red-bg)',
        borderRadius: '10px',
      }}
    >
      {message}
    </div>
  );
}

// Descargar un CSV a partir de filas (array de arrays).
export function descargarCSV(nombre, headers, rows) {
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
